import { css, htmlArray } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";

const styles: string = css`
   :host {
        display: block;
    }

    .quest-button {
        position: absolute;
        top: 50%;
        right: 0%;
        z-index: 1;
        background: none;
        border: none;
        padding: 0px;
        cursor: pointer;
        width: 50px;
        height: 50px;
    }
    
    .quest-button:hover {
        filter: brightness(0.5);
        scale: 1.1;
    }

    .quest-button img {
        width: 200%;
        height: 200%;
        object-fit: contain;
    }

    .quest-panel {
        display: none;
        position: fixed; /* Veranderd van absolute naar fixed */
        top: 50px;
        left: auto; /* Reset left */
        right: 20px; /* Aanpasbaar */
        width: 300px;
        background-color: rgba(20, 20, 20, 0.95);
        border: 2px solid #9f3f16;
        border-radius: 8px;
        padding: 15px;
        color: white;
        max-height: 400px;
        overflow-y: auto;
        z-index: 1000; /* Zorgt ervoor dat het paneel boven andere elementen komt */
    }


    .quest-panel.active {
        display: block;
    }

    .quest-title {
        text-align: center;
        font-size: 20px;
        margin-bottom: 15px;
        color: #963a13;
    }

    .quest-list {
        list-style-type: none;
        padding: 0;
        margin: 0;
    }

    .quest-item {
        padding: 15px;
        border-radius: 8px;
        margin-bottom: 12px;
    }


    .quest-item.completed {
        background-color: rgba(56, 142, 60, 0.2);
    }

    .quest-item-title {
        font-weight: bold;
        font-size: 18px;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .quest-status {
        font-size: 14px;
        font-weight: bold;
        padding: 4px 10px;
        border-radius: 4px;
    }

    .quest-description {
        margin-top: 8px;
        font-size: 14px;
    }

    .hidden {
        display: none;
    }

    .close-button {
        position: absolute;
        top: 10px;
        right: 10px;
        background: none;
        border: none;
        color: #9f3f16;
        font-size: 16px;
        cursor: pointer;
        padding: 5px;
    }
`;

type QuestArray = {
    NPC: string;
    startQuest: boolean;
    completed: boolean;
    description: string;
    reward: string;
};

export class QuestComponent extends HTMLElement {
    private readonly _gameRouteService: GameRouteService = new GameRouteService();
    private activeQuests: QuestArray[] = [];
    private _isPanelOpen: boolean = false;

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.render();
    }

    private renderQuests(): string {
        return this.activeQuests.map(quest => this.createQuestItem(quest)).join("");
    }

    private createQuestItem(quest: QuestArray): string {
        const statusClass: string = quest.completed ? "completed" : "active";
        const statusText: string = quest.completed ? "Completed" : "Active";
        const statusColor: string = quest.completed ? "green" : "#9f3f16"; // Kleuren aanpassen

        const capitalizedNPC: string = quest.NPC.charAt(0).toUpperCase() + quest.NPC.slice(1);
        return `
            <li class="quest-item ${statusClass}" style="background-color: ${
            quest.completed ? "rgba(56, 142, 60, 0.2)" : "rgba(198, 149, 42, 0.2)"
        };">
                <div class="quest-item-title">
                    <span class="quest-name">${capitalizedNPC}</span>
                    <span class="quest-status" style="background-color: ${statusColor}; color: white; padding: 2px 8px; border-radius: 4px;">
                        ${statusText}
                    </span>
                </div>
                <p class="quest-description">${quest.description}</p>
                <p class="quest-description">${quest.reward}</p>

            </li>
        `;
    }

    private render(): void {
        if (!this.shadowRoot) return;

        const questItemsHTML: string = this.renderQuests();
        const elements: HTMLElement[] = htmlArray`
            <style>${styles}</style>
            <button class="quest-button" id="questButton">
                <img src="/assets/img/QuestButton.png" alt="Quests">
            </button>
            <div class="quest-panel ${this._isPanelOpen ? "active" : ""}" id="questPanel">
                <button class="close-button" id="closeQuestPanel">✕</button>
                <div class="quest-title">Your Quests</div>
                <ul class="quest-list">
                    ${questItemsHTML}
                </ul>
            </div>
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }
        this.shadowRoot.append(...elements);

        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        if (!this.shadowRoot) return;

        const openButton: HTMLButtonElement | null = this.shadowRoot.querySelector("#questButton");
        const closeButton: HTMLButtonElement | null = this.shadowRoot.querySelector("#closeQuestPanel");

        if (openButton) {
            openButton.addEventListener("click", () => this.toggleQuestPanel());
        }

        if (closeButton) {
            closeButton.addEventListener("click", () => this.toggleQuestPanel());
        }
    }

    private async toggleQuestPanel(): Promise<void> {
        this._isPanelOpen = !this._isPanelOpen;

        if (this._isPanelOpen) {
            await this.handleGetActiveQuests();
        }

        this.render();
    }

    private async handleGetActiveQuests(): Promise<void> {
        try {
            const success: QuestArray[] = (await this._gameRouteService.executeGetQuests()) as QuestArray[];

            // Update alleen als de data echt is veranderd
            if (JSON.stringify(this.activeQuests) !== JSON.stringify(success)) {
                this.activeQuests = success;
                this.render();
            }
        }
        catch (error) {
            console.error("Error fetching active quests:", error);
        }
    }
}
